"use client";

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Container } from '@/components/design-system';
import { ArrowRight } from 'lucide-react';
import { useLocaleNavigation } from '@/lib/navigation';
import { CATEGORIES } from '@/constants/categories';
import { useLocale, useTranslations } from 'next-intl';

export default function Footer() {
    const { getNavigationPath } = useLocaleNavigation();
    const t = useTranslations('footer');
    const locale = useLocale();

    return (
        <footer className="border-t border-gray-800 bg-black/95 backdrop-blur supports-[backdrop-filter]:bg-black/90">
            <Container>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 py-12">
                    {/* Brand */}
                    <div className="space-y-6">
                        <div className="flex items-center space-x-2">
                            <Image src="/logo.svg" alt="FakeXGenerator" width={32} height={32} className="h-8 w-auto" />
                        </div>
                        <p className="text-sm text-gray-400 leading-relaxed">
                            {t('brand.description')}
                        </p>
                    </div>

                    {/* Categories */}
                    <div className="space-y-6">
                        <div className="flex items-center space-x-2">
                            <h3 className="text-lg font-semibold text-white">
                                {t('sections.categories')}
                            </h3>
                            <div className="h-px w-8 bg-gradient-to-r from-blue-600 to-transparent" />
                        </div>
                        <div className="space-y-3">
                            {Object.values(CATEGORIES).map((category) => (
                                <Link
                                    key={category.id}
                                    href={getNavigationPath(category.id)}
                                    className="group flex items-center justify-between text-sm text-gray-300 hover:text-white transition-all duration-200 hover:translate-x-1 p-2 rounded-lg hover:bg-white/5  hover:border-white/10"
                                >
                                    <span className="font-medium">{category.name[locale]}</span>
                                    <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                                </Link>
                            ))}
                        </div>
                    </div>

                    {/* Generators */}
                    <div className="space-y-6">
                        <div className="flex items-center space-x-2">
                            <h3 className="text-lg font-semibold text-white">
                                {t('sections.generators')}
                            </h3>
                            <div className="h-px w-8 bg-gradient-to-r from-purple-600 to-transparent" />
                        </div>
                        <div className="space-y-3">
                            <Link
                                href={getNavigationPath('names')}
                                className="group flex items-center justify-between text-sm text-gray-300 hover:text-white transition-all duration-200 hover:translate-x-1 p-2 rounded-lg hover:bg-white/5  hover:border-white/10"
                            >
                                <span className="font-medium">{CATEGORIES.personal.generators[0].name[locale]}</span>
                                <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                            </Link>
                        </div>
                    </div>

                    {/* Company */}
                    <div className="space-y-6">
                        <div className="flex items-center space-x-2">
                            <h3 className="text-lg font-semibold text-white">
                                {t('sections.company')}
                            </h3>
                            <div className="h-px w-8 bg-gradient-to-r from-blue-600 to-transparent" />
                        </div>
                        <div className="space-y-3">
                            <Link
                                href={getNavigationPath('about')}
                                className="group flex items-center justify-between text-sm text-gray-300 hover:text-white transition-all duration-200 hover:translate-x-1 p-2 rounded-lg hover:bg-white/5  hover:border-white/10"
                            >
                                <span className="font-medium">{t('links.aboutUs')}</span>
                                <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                            </Link>
                            <Link
                                href={getNavigationPath('privacyPolicy')}
                                className="group flex items-center justify-between text-sm text-gray-300 hover:text-white transition-all duration-200 hover:translate-x-1 p-2 rounded-lg hover:bg-white/5  hover:border-white/10"
                            >
                                <span className="font-medium">{t('links.privacyPolicy')}</span>
                                <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                            </Link>
                            <Link
                                href={getNavigationPath('termsOfUse')}
                                className="group flex items-center justify-between text-sm text-gray-300 hover:text-white transition-all duration-200 hover:translate-x-1 p-2 rounded-lg hover:bg-white/5  hover:border-white/10"
                            >
                                <span className="font-medium">{t('links.termsOfUse')}</span>
                                <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                            </Link>
                            <Link
                                href={getNavigationPath('blog')}
                                className="group flex items-center justify-between text-sm text-gray-300 hover:text-white transition-all duration-200 hover:translate-x-1 p-2 rounded-lg hover:bg-white/5  hover:border-white/10"
                            >
                                <span className="font-medium">Blog</span>
                                <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Bottom Section */}
                <div className="border-t border-gray-800 py-6">
                    <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
                        <p className="text-sm text-gray-400">
                            {t('copyright', { YEAR: new Date().getFullYear() })}
                        </p>
                        <div className="flex items-center space-x-6">
                            <Link
                                href={getNavigationPath('privacyPolicy')}
                                className="text-sm text-gray-400 hover:text-white transition-colors duration-200 p-2 rounded-lg hover:bg-white/5"
                            >
                                {t('links.privacyPolicy')}
                            </Link>
                            <Link
                                href={getNavigationPath('termsOfUse')}
                                className="text-sm text-gray-400 hover:text-white transition-colors duration-200 p-2 rounded-lg hover:bg-white/5"
                            >
                                {t('links.termsOfUse')}
                            </Link>
                        </div>
                    </div>
                </div>
            </Container>
        </footer>
    );
}
